import type { TailoredpayThreeDSResult } from '../constants';

/**
 * Payer authentication (3DS) for TailoredPay.
 *
 * Collect.js cannot do 3DS — it needs Gateway.js, which is a separate script
 * with its own public key. The flow is also INVERTED compared with Stripe and
 * Recurly: here we authenticate BEFORE sending the transaction and pass the
 * resulting cavv/eci to the backend, instead of submitting first and reacting to
 * a `requires_action` / `three_d_secure_action_token_id` response.
 */

export const THREE_DS_MOUNT_ID = 'tailoredpay-3ds-mount';

/**
 * How long to wait for Gateway.js to either start a challenge or return a result.
 * Covers setup only: once the cardholder is in front of the issuer's form there is
 * no deadline.
 */
const SETUP_TIMEOUT_MS = 15000;

type AuthenticateParams = {
  gatewayKey: string;
  paymentToken: string;
  /** Amount actually charged now (the trial amount for a trial checkout). */
  amount: string | number;
  currency: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  address1?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  /** Selector of the element the challenge iframe mounts into. */
  mountSelector?: string;
  onChallenge?: () => void;
};

const omitEmpty = <T extends Record<string, any>>(obj: T) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null && v !== ''));

/**
 * Runs payer authentication and resolves with the fields the backend needs in
 * `three_ds`. Resolves with `null` when 3DS cannot run at all — the caller
 * decides whether to continue without it (the gateway may still accept the
 * transaction and simply carry the liability).
 */
export const authenticateThreeDS = ({
  gatewayKey,
  paymentToken,
  amount,
  currency,
  email,
  firstName,
  lastName,
  phone,
  address1,
  city,
  state,
  postalCode,
  country,
  mountSelector = `#${THREE_DS_MOUNT_ID}`,
  onChallenge,
}: AuthenticateParams): Promise<TailoredpayThreeDSResult | null> => {
  const GatewayFactory = (window as any).Gateway;

  if (!GatewayFactory || !gatewayKey) {
    return Promise.resolve(null);
  }

  // Gateway.js requires currency, amount, firstName and lastName, and rejects
  // empty strings for them (`_isValid`). Without all four `createUI` returns null
  // instead of a UI object. Our name inputs are not mandatory, so check here and
  // skip authentication rather than failing the payment over it.
  if (!firstName || !lastName) {
    console.warn(
      '[TailoredPay] skipping 3DS — Gateway.js requires a non-empty firstName and '
      + 'lastName, and the card form left them empty',
    );
    return Promise.resolve(null);
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    let setupTimeoutId: number | undefined;
    let mountedUi: any = null;

    /**
     * Gateway.js tracks the mounted 3DS UI in a global singleton
     * (`GatewayJSThreeDSecureMountedUISingleton`) and refuses to work when another
     * UI is still registered. Leaving one behind is what turns a second attempt
     * into a reused session — so every exit path tears it down, not just success.
     */
    const unmountUi = () => {
      if (!mountedUi) return;
      const ui = mountedUi;
      mountedUi = null;
      try {
        if (ui.isMounted?.()) {
          ui.unmount();
        }
      } catch (e) {
        console.warn('[TailoredPay] could not unmount the 3DS UI', e);
      }
    };

    const clearSetupTimeout = () => {
      if (setupTimeoutId !== undefined) {
        window.clearTimeout(setupTimeoutId);
        setupTimeoutId = undefined;
      }
    };

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearSetupTimeout();
      unmountUi();
      fn();
    };

    /**
     * Give up on authentication and let the payment through. Every 3DS problem that
     * is OUR side — a wrong or missing key, a payload Gateway.js rejects, a script
     * that never answers — lands here: the cardholder cannot fix any of it, and
     * failing the payment over a misconfiguration is worse than sending the
     * transaction unauthenticated and letting the gateway decide (it then carries
     * the liability). Only a genuine authentication FAILURE rejects, below.
     */
    const skip = (reason: string, detail?: any) => finish(() => {
      console.warn(`[TailoredPay] skipping 3DS — ${reason}`, detail ?? '');
      resolve(null);
    });

    try {
      const gateway = GatewayFactory.create(gatewayKey);
      const threeDS = gateway.get3DSecure();

      gateway.on('error', (e: any) => {
        skip('Gateway.js reported an error', e?.error || e);
      });

      // Option validation ("Unknown options", "must be provided") is reported on
      // the threeDS object, not on the gateway — without this listener a rejected
      // payload would be invisible and only show up as a null from createUI.
      threeDS.on('error', (e: any) => {
        skip('Gateway.js rejected the request', e?.error || e);
      });

      const ui = threeDS.createUI(
        omitEmpty({
          paymentToken,
          amount: String(amount),
          currency,
          email,
          firstName,
          lastName,
          phone,
          address1,
          city,
          state,
          postalCode,
          country,
        }),
      );

      // Null means the payload was rejected; the reason has usually already arrived
      // through the `error` listener above, which settles this promise first.
      if (!ui) {
        skip('Gateway.js returned no UI for the request');
        return;
      }

      mountedUi = ui;

      // Listeners before start(), so an event fired synchronously is not missed.

      // The UI has its own `error` channel, separate from the gateway and the
      // service: anything the 3DS provider itself rejects (an invalid JWT, a bad
      // lookup) is reported here. Without this listener such a failure was invisible
      // to us and the promise sat until the setup deadline expired.
      ui.on('error', (e: any) => {
        skip('the 3DS provider rejected the request', e?.error || e?.message || e);
      });

      ui.on('challenge', () => {
        // The cardholder is now interacting with the issuer's form, which can take
        // as long as it takes — the setup deadline no longer applies.
        clearSetupTimeout();
        onChallenge?.();
      });

      ui.on('complete', (e: any) => {
        // The event also carries `cardHolderInfo` — a message from the issuer meant
        // to be SHOWN to the cardholder and, per NMI's docs, never passed into the
        // API. It is deliberately left out of the result below; log it so it is at
        // least not lost while nothing renders it.
        if (e?.cardHolderInfo) {
          console.info('[TailoredPay] 3DS cardholder message:', e.cardHolderInfo);
        }

        finish(() =>
          resolve({
            cavv: e?.cavv,
            xid: e?.xid,
            eci: e?.eci,
            // Gateway.js returns camelCase; the backend contract is snake_case.
            cardholder_auth: e?.cardHolderAuth,
            three_ds_version: e?.threeDsVersion,
            directory_server_id: e?.directoryServerId,
          }),
        );
      });

      // The one path that must NOT be skipped: the issuer answered and refused to
      // authenticate the cardholder. Sending that transaction anyway is exactly what
      // 3DS exists to prevent.
      ui.on('failure', (e: any) => {
        finish(() =>
          reject(
            Object.assign(new Error(e?.message || 'Payer authentication failed'), {
              code: e?.code,
              threeDS: true,
            }),
          ),
        );
      });

      ui.start(mountSelector);

      // A wrong key can leave Gateway.js silent rather than erroring, and without a
      // deadline the submit would hang forever with the button stuck in loading.
      // Only covers setup — cleared as soon as a challenge starts.
      setupTimeoutId = window.setTimeout(() => {
        skip(`no response from Gateway.js within ${SETUP_TIMEOUT_MS}ms`);
      }, SETUP_TIMEOUT_MS);
    } catch (e: any) {
      skip('Gateway.js threw while starting authentication', e);
    }
  });
};
